import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take, tap } from 'rxjs/operators';
import { AuthConfigService } from './auth-config.service';
import { AuthService } from './auth.service';
import { TokenStorageService } from './token-storage.service';
import { TokenRefreshService } from './token-refresh.service';

@Injectable({
  providedIn: 'root',
})
export class AuthFlowService {
  private tokenStorage = inject(TokenStorageService);
  private authService = inject(AuthService);
  private config = inject(AuthConfigService);
  private router = inject(Router);
  private tokenRefreshService = inject(TokenRefreshService);

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(
    null,
  );

  handleTokenRefresh(): Observable<string> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.tokenRefreshService.refreshToken().pipe(
        tap(() => {
          const newToken = this.tokenStorage.getToken();
          this.refreshTokenSubject.next(newToken);
        }),
        switchMap(() => {
          const token = this.tokenStorage.getToken();
          if (!token) {
            throw new Error('Token no disponible después del refresh');
          }
          return new Observable<string>((subscriber) => {
            subscriber.next(token);
            subscriber.complete();
          });
        }),
        catchError((error) => {
          console.error('Error al renovar el token:', error);
          return this.handleAuthenticationFailure();
        }),
        finalize(() => {
          this.isRefreshing = false;
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          if (!token) {
            throw new Error('Token no disponible');
          }
          return new Observable<string>((subscriber) => {
            subscriber.next(token);
            subscriber.complete();
          });
        }),
      );
    }
  }

  getCurrentToken(): string | null {
    return this.tokenStorage.getToken();
  }

  getTokenConfig() {
    return this.config.getConfig();
  }

  private handleAuthenticationFailure(): Observable<never> {
    return this.authService.logout().pipe(
      tap(() => {
        this.router.navigate(['/login']).then((r) => r);
      }),
      switchMap(() =>
        throwError(() => new Error('Sesión expirada, por favor inicie sesión nuevamente.')),
      ),
    );
  }
}
